// Grass: clumps of blades, instanced as clumps.
//
// The unit of instancing is deliberately NOT a blade. A blade per instance
// is what kept the meadow thin: the instance matrix buffer is re-uploaded
// in full whenever a single instance moves, and world/chunks.ts moves
// instances every time a column streams in, so blade count and streaming
// cost are the same number. Baking ~28 blades into one geometry and
// instancing that decouples them — the buffer stays around a thousand
// entries while the field carries thirty thousand blades, still in one
// draw call, and a column load uploads exactly what it did before.
//
// The blades still live in chunks.ts's InstancedPool, so streaming, the
// free-list and the per-instance colour are all untouched. What's here is
// the clump geometry and the vertex patch that bends it.
//
// All of the motion is vertex-shader displacement, so a meadow costs no
// per-frame CPU at all — updateGrass writes four uniforms and stops.

import * as THREE from 'three';
import { toonVertexColorMat } from '@world/toon';
import { surfaceYAt } from '@world/terrain';
import { ENV } from '@world/weather';
import { player } from '@entities/player';

// Blades per clump, and segments per blade. Both are multiplied by every
// instance placed, so they're the two numbers to turn if the triangle
// count needs to come down: a clump is BLADES * (SEGMENTS * 2 - 1)
// triangles, and chunks.ts places a handful per hex.
const CLUMP_BLADES = 28;
const BLADE_SEGMENTS = 4;
const BLADE_WIDTH = 0.055;

// The clump is authored in a unit disc, one unit tall, so an instance's
// scale reads directly as (radius, height, radius) in world units.
//
// Per-vertex `aBlade` carries what the shader can no longer get from the
// instance, now that an instance is a clump rather than a blade:
//   xy = the blade's root in clump space — its own wind phase, and where
//        the fisherman's wake is measured from
//   z  = 0..1 up the blade, which drives the bend
//   w  = the blade's height, so a short blade in a clump bends less than
//        the tall one beside it
export function buildGrassClumpGeometry(seed = 0x9e37): THREE.BufferGeometry {
  // A small LCG rather than Math.random, so the clump is identical run to
  // run and a screenshot can be compared against the last one.
  let s = seed >>> 0;
  const rnd = () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);

  const pos: number[] = [], nor: number[] = [], col: number[] = [];
  const blade: number[] = [], idx: number[] = [];
  const n = new THREE.Vector3();

  for (let b = 0; b < CLUMP_BLADES; b++) {
    // sqrt keeps the scatter even across the disc instead of crowding the
    // middle, which is what a raw uniform radius does.
    const rad = Math.sqrt(rnd()) * 0.95;
    const ang = rnd() * Math.PI * 2;
    const rx = Math.cos(ang) * rad, rz = Math.sin(ang) * rad;
    const rot = rnd() * Math.PI * 2;
    const cs = Math.cos(rot), sn = Math.sin(rot);
    const height = 0.55 + rnd() * 0.65;
    // Every blade arcs over rather than standing straight. This is baked,
    // not animated: it's the blade's own habit, and the wind bends it
    // further on top.
    const leanA = rnd() * Math.PI * 2;
    const lean = (0.1 + rnd() * 0.3) * height;
    const lx = Math.cos(leanA) * lean, lz = Math.sin(leanA) * lean;
    // Per-blade brightness, multiplied by the per-hex colour the pool
    // supplies, so blades inside one clump don't all read as one solid mass.
    const tone = 0.86 + rnd() * 0.28;

    const base = pos.length / 3;
    const put = (hx: number, t: number, nx: number) => {
      const arc = t * t;
      pos.push(rx + hx * cs + lx * arc, t * height, rz - hx * sn + lz * arc);
      n.set(nx, 0.22, 1).normalize();
      // The blade's own rotation has to reach its normals too, or every
      // blade in the clump lights as though it faced the same way.
      nor.push(n.x * cs + n.z * sn, n.y, -n.x * sn + n.z * cs);
      // Dark at the root, pale at the tip. This is the cheapest thing that
      // reads as depth in a mass of grass — without it a clump is a flat
      // green patch however many blades are in it.
      const shade = (0.56 + 0.52 * t) * tone;
      col.push(shade, shade * 1.02, shade * 0.94);
      blade.push(rx, rz, t, height);
    };
    for (let i = 0; i < BLADE_SEGMENTS; i++) {
      const t = i / BLADE_SEGMENTS;
      // Convex taper: a blade holds its width most of the way up and then
      // runs quickly to a point. A linear taper is a triangle and reads
      // as one.
      const w = BLADE_WIDTH * Math.pow(1 - t, 0.7);
      put(-w, t, -0.35);
      put(w, t, 0.35);
    }
    put(0, 1, 0); // tip
    for (let i = 0; i < BLADE_SEGMENTS - 1; i++) {
      const a = base + i * 2;
      idx.push(a, a + 1, a + 3, a, a + 3, a + 2);
    }
    const last = base + (BLADE_SEGMENTS - 1) * 2;
    idx.push(last, last + 1, base + BLADE_SEGMENTS * 2);
  }

  // Normalise the disc to radius 1. The baked lean carries blade tips well
  // past wherever their roots were scattered — measured at 1.19 before this
  // — and chunks.ts sizes its placement offsets against the clump radius to
  // keep grass inside its own hex. An instance scale that doesn't mean what
  // it says is how grass ends up on the sand next door.
  let maxR = 0;
  for (let i = 0; i < pos.length; i += 3) maxR = Math.max(maxR, Math.hypot(pos[i], pos[i + 2]));
  if (maxR > 0) {
    for (let i = 0; i < pos.length; i += 3) { pos[i] /= maxR; pos[i + 2] /= maxR; }
    // The roots travel with them: the shader measures the fisherman's wake
    // from aBlade.xy, so it has to stay the blade's actual position.
    for (let i = 0; i < blade.length; i += 4) { blade[i] /= maxR; blade[i + 1] /= maxR; }
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  // InstancedPool's per-instance colour multiplies this, so the per-hex
  // green and the per-blade shading compose without either needing to know
  // about the other.
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('aBlade', new THREE.Float32BufferAttribute(blade, 4));
  g.setIndex(idx);
  return g;
}

// Shared by reference with the compiled material, so updateGrass mutating
// .value is all it takes to move the whole meadow.
const uniforms = {
  uTime: { value: 0 },
  // Direction and strength together — a wind vector, not a heading.
  uWind: { value: new THREE.Vector2(0.22, 0.09) },
  uPlayer: { value: new THREE.Vector3(1e6, 0, 1e6) },
  uPlayerR: { value: 0.62 },
};

// Same vocabulary main.ts already uses for sea choppiness, so the grass,
// the swell and the rain all agree about how rough the day is.
const WIND_BY_WEATHER: Record<string, number> = { Storm: 3.4, Rain: 1.7, Cloudy: 1.25, Clear: 1 };
const WIND_DEFAULT = 1.1;
// Eased rather than snapped, so weather changing at a day boundary doesn't
// jerk every blade on the island at once.
let windNow = 1;

export function grassMat(): THREE.MeshToonMaterial {
  const m = toonVertexColorMat();
  // A blade is a flat sheet — it has to be lit and drawn from both sides.
  m.side = THREE.DoubleSide;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
attribute vec4 aBlade;
uniform float uTime;
uniform vec2 uWind;
uniform vec3 uPlayer;
uniform float uPlayerR;`)
      // Guarded on USE_INSTANCING because the whole block reads
      // instanceMatrix, and the splice point sits outside three's own
      // #ifdef — on a plain mesh this material would otherwise fail to
      // compile rather than just standing still.
      //
      // Spliced between the instance transform and the view transform, so
      // the bend happens in world space. Bending the local position instead
      // would send every blade a different way, since place() gives each
      // clump a random Y rotation — the wind has to be coherent across a
      // field, not per clump.
      .replace('mvPosition = modelViewMatrix * mvPosition;', `
#ifdef USE_INSTANCING
{
  // groundCoverGroup sits at the origin untransformed, so object space is
  // world space here. The blade's own root has to be transformed through
  // the instance matrix now that an instance is a clump — taking the
  // clump's translation column instead would sway all 28 blades in
  // lockstep and measure the fisherman's wake from the clump's middle.
  vec3 root = ( instanceMatrix * vec4( aBlade.x, 0.0, aBlade.y, 1.0 ) ).xyz;
  float t = aBlade.z;
  // Squared, so the root stays planted in the ground and the displacement
  // accumulates toward the tip. Scaled by this blade's own height, so a
  // short blade leans less than the tall one next to it.
  float bend = t * t * aBlade.w * length( instanceMatrix[1].xyz );

  // Phase off the world position, so a gust travels across the field
  // instead of every blade breathing in unison.
  float gust = 0.68 + 0.32 * sin( uTime * 1.5 + root.x * 1.7 + root.z * 2.3 );
  mvPosition.xz += uWind * gust * bend;

  // A wake around the fisherman: blades lean away as he wades through.
  vec2 away = root.xz - uPlayer.xz;
  float dist = length( away );
  float near = smoothstep( uPlayerR, uPlayerR * 0.2, dist );
  // Only grass at roughly his own level — without this he flattens the
  // tops of hexes two levels below him as he walks along a ridge.
  near *= 1.0 - smoothstep( 0.12, 0.3, abs( root.y - uPlayer.y ) );
  mvPosition.xz += ( away / max( dist, 1e-4 ) ) * near * bend * 1.7;
}
#endif
mvPosition = modelViewMatrix * mvPosition;`);
  };
  // The other cover pools are MeshToonMaterials with identical parameters,
  // and three caches programs by those — without a key of its own they would
  // be handed this patched program, and their geometry has no aBlade.
  m.customProgramCacheKey = () => 'grass-clump';
  return m;
}

// Normals are deliberately left unbent by the wind. Re-deriving them per
// vertex would cost more than it shows: the toon ramp is three near-white
// bands, so a blade leaning over barely changes which one it lands in.

export function updateGrass(now: number, dt: number): void {
  uniforms.uTime.value = now * 0.001;
  const target = WIND_BY_WEATHER[ENV.weather] ?? WIND_DEFAULT;
  windNow += (target - windNow) * Math.min(1, dt * 0.6);
  uniforms.uWind.value.set(0.22 * windNow, 0.09 * windNow);
  uniforms.uPlayer.value.set(player.x, surfaceYAt(player.x, player.z), player.z);
}
